export const broadcastVariableOwnershipOptions = ['crm', 'campaign'] as const;

export type BroadcastVariableOwnership = (typeof broadcastVariableOwnershipOptions)[number];

export const broadcastAudienceSourceOptions = ['leadflow', 'saved_contact_lists'] as const;
export type BroadcastAudienceSource = (typeof broadcastAudienceSourceOptions)[number];

export type BroadcastCrmVariable = {
  key: string;
  label: string;
  description: string;
  protected: boolean;
};

export const broadcastCrmVariables = [
  {
    key: 'lead_name',
    label: 'Lead Name',
    description: 'LeaderDaddy lead name or saved contact name.',
    protected: true,
  },
  {
    key: 'first_name',
    label: 'First Name',
    description: 'First word from the lead or contact name.',
    protected: true,
  },
  {
    key: 'last_name',
    label: 'Last Name',
    description: 'Remaining words from the lead or contact name.',
    protected: true,
  },
  {
    key: 'phone',
    label: 'Phone',
    description: 'Resolved WhatsApp mobile number.',
    protected: true,
  },
  {
    key: 'email',
    label: 'Email',
    description: 'Lead or saved contact email address.',
    protected: true,
  },
  {
    key: 'company',
    label: 'Company',
    description: 'Lead company, product context, or workspace business name.',
    protected: true,
  },
  {
    key: 'owner_name',
    label: 'Owner Name',
    description: 'Assigned LeaderDaddy owner or sender name.',
    protected: true,
  },
  {
    key: 'product_name',
    label: 'Product Name',
    description: 'Product linked to the lead or template.',
    protected: false,
  },
  {
    key: 'business_name',
    label: 'Business Name',
    description: 'Connected WhatsApp business or workspace name.',
    protected: false,
  },
  {
    key: 'campaign_name',
    label: 'Campaign Name',
    description: 'Broadcast campaign name.',
    protected: false,
  },
  {
    key: 'sender_name',
    label: 'Sender Name',
    description: 'User launching the campaign.',
    protected: false,
  },
  {
    key: 'current_date',
    label: 'Current Date',
    description: 'Send date in ISO format.',
    protected: false,
  },
  {
    key: 'lead_status',
    label: 'Lead Status',
    description: 'Current LeaderDaddy status.',
    protected: false,
  },
  {
    key: 'lead_source',
    label: 'Lead Source',
    description: 'LeaderDaddy source field.',
    protected: false,
  },
  {
    key: 'lead_address',
    label: 'Lead Address',
    description: 'LeaderDaddy address field.',
    protected: false,
  },
  {
    key: 'created_by_name',
    label: 'Created By',
    description: 'User who created the LeaderDaddy lead.',
    protected: false,
  },
] as const satisfies readonly BroadcastCrmVariable[];

export const protectedBroadcastCrmVariableKeys = broadcastCrmVariables
  .filter(variable => variable.protected)
  .map(variable => variable.key);

export const broadcastCrmVariableAliases: Record<string, string> = {
  leadName: 'lead_name',
  customerName: 'lead_name',
  recipientName: 'lead_name',
  recipient_name: 'lead_name',
  firstName: 'first_name',
  lastName: 'last_name',
  leadPhone: 'phone',
  lead_phone: 'phone',
  phoneNumber: 'phone',
  phone_number: 'phone',
  leadEmail: 'email',
  lead_email: 'email',
  leadCompany: 'company',
  lead_company: 'company',
  ownerName: 'owner_name',
  assignedUserName: 'owner_name',
  assigned_user_name: 'owner_name',
  productName: 'product_name',
  product_name: 'product_name',
  businessName: 'business_name',
  business_name: 'business_name',
  campaignName: 'campaign_name',
  campaign_name: 'campaign_name',
  senderName: 'sender_name',
  sender_name: 'sender_name',
  currentDate: 'current_date',
  current_date: 'current_date',
  leadStatus: 'lead_status',
  lead_status: 'lead_status',
  leadSource: 'lead_source',
  lead_source: 'lead_source',
  leadAddress: 'lead_address',
  lead_address: 'lead_address',
  createdByName: 'created_by_name',
  created_by_name: 'created_by_name',
  var1: 'lead_name',
  bodyVar1: 'lead_name',
  headerVar1: 'lead_name',
};

const broadcastCrmVariableKeySet = new Set<string>(
  broadcastCrmVariables.map(variable => variable.key)
);
const protectedBroadcastCrmVariableKeySet = new Set<string>(protectedBroadcastCrmVariableKeys);
const savedContactBroadcastVariableKeySet = new Set([
  'lead_name',
  'first_name',
  'last_name',
  'phone',
  'business_name',
  'campaign_name',
  'sender_name',
  'current_date',
]);

export function normalizeBroadcastCrmVariableKey(key: string) {
  const trimmedKey = key.trim();
  const alias = broadcastCrmVariableAliases[trimmedKey];

  if (alias) {
    return alias;
  }

  return broadcastCrmVariableKeySet.has(trimmedKey) ? trimmedKey : null;
}

export function isBroadcastCrmVariableKey(key: string) {
  return normalizeBroadcastCrmVariableKey(key) !== null;
}

export function isProtectedBroadcastCrmVariableKey(key: string) {
  const normalizedKey = normalizeBroadcastCrmVariableKey(key) ?? key.trim();
  return protectedBroadcastCrmVariableKeySet.has(normalizedKey);
}

export function inferBroadcastVariableOwnership(key: string): BroadcastVariableOwnership {
  return isBroadcastCrmVariableKey(key) ? 'crm' : 'campaign';
}

export function isBroadcastVariableSupportedForAudience(
  key: string,
  audienceSource: BroadcastAudienceSource
) {
  const normalizedKey = normalizeBroadcastCrmVariableKey(key);

  if (!normalizedKey || audienceSource === 'leadflow') {
    return true;
  }

  return savedContactBroadcastVariableKeySet.has(normalizedKey);
}
